// Web Storage that never crashes the page.
//
// `window.localStorage` / `window.sessionStorage` are NOT guaranteed to exist or
// be usable. In several environments the property is `null` (some Android
// in-app browsers / WebViews with storage disabled) or *throws* on access
// (`SecurityError` in a sandboxed iframe, some private-mode browsers). A bare
// `localStorage.getItem(k)` there throws `Cannot read properties of null
// (reading 'getItem')` — an uncaught TypeError that breaks hydration for exactly
// the mobile ad traffic (Yandex/VK in-app browsers) we most need to keep on the
// page. Route every storage access through these helpers instead of touching
// `localStorage`/`sessionStorage` directly; they degrade to a null read / no-op
// write when storage is unavailable. Guard: tests/unit/safe-storage.test.ts.

type StorageKind = "local" | "session";

function resolveStorage(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    // Accessing the property itself can throw (sandboxed iframe SecurityError),
    // and can be `null` (storage disabled) — both handled here.
    const storage = kind === "local" ? window.localStorage : window.sessionStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

export function readStorage(kind: StorageKind, key: string): string | null {
  const storage = resolveStorage(kind);
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStorage(kind: StorageKind, key: string, value: string): void {
  const storage = resolveStorage(kind);
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded / private-mode write rejection — best-effort persistence.
  }
}

export function removeStorage(kind: StorageKind, key: string): void {
  const storage = resolveStorage(kind);
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}
